import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import MapboxMap from '../components/MapboxMap';
import SEO from '../components/SEO';
import { translatePropertyType } from '../utils/translations';
import MortgageCalculator from '../components/MortgageCalculator';
import './PropertyDetail.css';

const TRANSITION_DURATION = 150;
const MIN_SWIPE_DISTANCE = 50;

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageTransition, setImageTransition] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');
  const galleryViewportRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`/api/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const galleryImages = property?.images?.length
    ? property.images
    : property?.mainImage
      ? [property.mainImage]
      : [];

  useEffect(() => {
    setSelectedImage(0);
    setImageTransition(false);
    setLightboxOpen(false);

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, [id]);

  // Helper function to change image with transition
  const selectImageWithTransition = useCallback((index) => {
    if (index === selectedImage) return;

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    setImageTransition(true);
    transitionTimeoutRef.current = setTimeout(() => {
      setSelectedImage(index);
      setImageTransition(false);
    }, TRANSITION_DURATION);
  }, [selectedImage]);

  const scrollGalleryToImage = useCallback((index) => {
    const viewport = galleryViewportRef.current;
    if (!viewport) return;

    const slide = viewport.children[index];
    if (!slide) return;

    isProgrammaticScrollRef.current = true;
    viewport.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });

    window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, TRANSITION_DURATION + 100);
  }, []);

  useEffect(() => {
    scrollGalleryToImage(selectedImage);
  }, [selectedImage, scrollGalleryToImage]);

  // Navigate to next image with looping
  const nextImage = useCallback(() => {
    if (galleryImages.length <= 1) return;
    
    const newIndex = (selectedImage + 1) % galleryImages.length;
    selectImageWithTransition(newIndex);
  }, [galleryImages.length, selectedImage, selectImageWithTransition]);

  // Navigate to previous image with looping
  const previousImage = useCallback(() => {
    if (galleryImages.length <= 1) return;
    
    const newIndex = selectedImage === 0 ? galleryImages.length - 1 : selectedImage - 1;
    selectImageWithTransition(newIndex);
  }, [galleryImages.length, selectedImage, selectImageWithTransition]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        previousImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'Escape' && lightboxOpen) {
        setLightboxOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextImage, previousImage, lightboxOpen]);

  const handleGalleryScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) return;

    const viewport = galleryViewportRef.current;
    if (!viewport || galleryImages.length === 0) return;

    const nextIndex = Math.round(viewport.scrollLeft / viewport.clientWidth);
    const boundedIndex = Math.max(0, Math.min(nextIndex, galleryImages.length - 1));

    if (boundedIndex !== selectedImage) {
      setSelectedImage(boundedIndex);
    }
  }, [galleryImages.length, selectedImage]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const dx = touchStartX.current - touchEndX.current;
    if (Math.abs(dx) >= MIN_SWIPE_DISTANCE) {
      if (dx > 0) {
        nextImage();
      } else {
        previousImage();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [nextImage, previousImage]);

  const formatPrice = (price, listingType) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
    
    return listingType === 'for-rent' ? `${formatted}/mes` : formatted;
  };

  const handleContactChange = (e) => {
    setContactFormData({
      ...contactFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    setContactSubmitted(false);

    const web3formsAccessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

    if (!web3formsAccessKey) {
      setContactError('Error de configuración. Por favor, contacte al administrador.');
      setContactLoading(false);
      return;
    }

    try {
      const formPayload = {
        access_key: web3formsAccessKey,
        name: contactFormData.name,
        email: contactFormData.email,
        phone: contactFormData.phone || 'No proporcionado',
        message: `${contactFormData.message}\n\nPropiedad de interés: ${property.title}`,
        subject: `ARG Pagina Web - ${contactFormData.name}`,
        from_name: 'Alicea Realty Group Website',
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formPayload),
      });

      const result = await response.json();

      if (result.success) {
        setContactSubmitted(true);
        setContactFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setContactError('Hubo un problema al enviar el mensaje. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Property contact form submission error:', error);
      setContactError('Error al enviar el mensaje. Por favor, verifique su conexión e intente nuevamente.');
    } finally {
      setContactLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando detalles de la propiedad...</div>;
  }

  if (!property) {
    return <div className="error">Propiedad no encontrada</div>;
  }

  // Default listingType to 'for-sale' if not set
  const listingType = property.listingType || 'for-sale';

  const mapStyle = {
    width: '100%',
    height: '400px',
  };

  const center = {
    lat: property.coordinates?.lat || 0,
    lng: property.coordinates?.lng || 0,
  };

  return (
    <div className="property-detail">
      <SEO 
        title={`${property.title} | ${property.city ? property.city + ', ' : ''}Puerto Rico | Alicea Realty Group LLC`}
        description={`${property.description ? property.description.substring(0, 150) : `${translatePropertyType(property.propertyType)} en ${property.city || 'Puerto Rico'}`}. ${formatPrice(property.price, listingType)}. ${property.bedrooms} hab, ${property.bathrooms} baños. Contacte a Noemí Alicea González.`}
        keywords={`${property.city || 'Puerto Rico'}, ${translatePropertyType(property.propertyType)}, ${property.listingType === 'for-rent' ? 'alquiler' : 'venta'} ${property.city || 'Puerto Rico'}, propiedad ${property.city || 'Puerto Rico'}, ${property.bedrooms} habitaciones`}
        ogTitle={`${property.title} | Alicea Realty Group LLC`}
        ogDescription={`${translatePropertyType(property.propertyType)} en ${property.city || 'Puerto Rico'}. ${formatPrice(property.price, listingType)}. ${property.bedrooms} hab, ${property.bathrooms} baños.`}
        ogImage={property.mainImage || (property.images && property.images.length > 0 ? property.images[0] : 'https://firebasestorage.googleapis.com/v0/b/realty-website-764ee.firebasestorage.app/o/web-media%2FPicture2.png?alt=media&token=84ce00a9-afce-4158-945c-03a03c30254b')}
        canonical={`https://www.alicearealtypr.com/property/${id}`}
        type="product"
      />
      
      <div className="property-images">
        <div className="main-image">
          <div
            className="image-carousel"
            ref={galleryViewportRef}
            onScroll={handleGalleryScroll}
          >
            {galleryImages.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                className={`image-slide ${selectedImage === index ? 'active' : ''} ${imageTransition && selectedImage === index ? 'image-transition' : ''}`}
                onClick={() => setLightboxOpen(true)}
                aria-label={`Open image ${index + 1} in lightbox`}
              >
                <img
                  src={image}
                  alt={`${property.title} ${index + 1}`}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23ddd" width="800" height="600"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              </button>
            ))}
          </div>
          {galleryImages.length > 1 && (
            <>
              <button
                className="image-nav-btn prev-btn"
                onClick={previousImage}
                aria-label="Previous image"
              >
                &#8249;
              </button>
              <button
                className="image-nav-btn next-btn"
                onClick={nextImage}
                aria-label="Next image"
              >
                &#8250;
              </button>
              <div className="image-counter">
                {selectedImage + 1} / {galleryImages.length}
              </div>
            </>
          )}
        </div>
        {galleryImages.length > 1 && (
          <div className="image-indicators" aria-label="Image navigation">
            {galleryImages.map((_, index) => (
              <button
                key={`indicator-${index}`}
                type="button"
                className={`image-indicator ${selectedImage === index ? 'active' : ''}`}
                onClick={() => selectImageWithTransition(index)}
                aria-label={`View image ${index + 1}`}
                aria-current={selectedImage === index ? 'true' : 'false'}
              />
            ))}
          </div>
        )}
        {galleryImages.length > 1 && (
          <div className="image-thumbnails">
            {galleryImages.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`${property.title} ${index + 1}`}
                className={selectedImage === index ? 'active' : ''}
                onClick={() => selectImageWithTransition(index)}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="100"%3E%3Crect fill="%23ddd" width="150" height="100"/%3E%3C/svg%3E';
                }}
              />
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={galleryImages[selectedImage] || property.mainImage}
              alt={property.title}
              className="lightbox-img"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23ddd" width="800" height="600"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
          <button
            className="lightbox-close"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close lightbox"
          >
            &#10005;
          </button>
          {galleryImages.length > 1 && (
            <>
              <button
                className="lightbox-nav-btn lightbox-prev"
                onClick={(e) => { e.stopPropagation(); previousImage(); }}
                aria-label="Previous image"
              >
                &#8249;
              </button>
              <button
                className="lightbox-nav-btn lightbox-next"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                aria-label="Next image"
              >
                &#8250;
              </button>
              <div className="lightbox-counter">
                {selectedImage + 1} / {galleryImages.length}
              </div>
            </>
          )}
        </div>
      )}

      <div className={`property-content ${listingType === 'for-sale' ? 'with-sidebar' : ''}`}>
        <div className="property-info">
          <div className="property-header">
            <div>
              <h1>{property.title}</h1>
              <p className="property-address">
                {property.address}, {property.city}, {property.state} {property.zipCode}
              </p>
              {property.isReposeida && (
                <span className="reposeida-tag">Reposeida</span>
              )}
            </div>
            <div className="property-price">{formatPrice(property.price, listingType)}</div>
          </div>

          <div className="property-stats">
            <div className="stat">
              <div className="stat-icon"></div>
              <div className="stat-info">
                <div className="stat-value">{property.bedrooms}</div>
                <div className="stat-label">Habitaciones</div>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon"></div>
              <div className="stat-info">
                <div className="stat-value">{property.bathrooms}</div>
                <div className="stat-label">Baños</div>
              </div>
            </div>
            {property.lotSizeM2 && property.lotSizeM2 > 0 && (
              <div className="stat">
                <div className="stat-icon"></div>
                <div className="stat-info">
                  <div className="stat-value">{property.lotSizeM2}</div>
                  <div className="stat-label">m²</div>
                </div>
              </div>
            )}
            {property.area && property.area > 0 && (
              <div className="stat">
                <div className="stat-icon"></div>
                <div className="stat-info">
                  <div className="stat-value">{property.area}</div>
                  <div className="stat-label">ft²</div>
                </div>
              </div>
            )}
            {property.yearBuilt && property.yearBuilt > 0 && (
              <div className="stat">
                <div className="stat-icon"></div>
                <div className="stat-info">
                  <div className="stat-value">{property.yearBuilt}</div>
                  <div className="stat-label">Año de Construcción</div>
                </div>
              </div>
            )}
            <div className="stat">
              <div className="stat-icon"></div>
              <div className="stat-info">
                <div className="stat-value">{translatePropertyType(property.propertyType)}</div>
                <div className="stat-label">Tipo</div>
              </div>
            </div>
          </div>

          <div className="property-description">
            <h2>Descripción</h2>
            <p>{property.description}</p>
          </div>

          {property.features && property.features.length > 0 && (
            <div className="property-features">
              <h2>Características</h2>
              <ul>
                {property.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="property-map">
            <h2>Ubicación</h2>
            {property.coordinates?.lat && property.coordinates?.lng && 
             Number(property.coordinates.lat) !== 0 && Number(property.coordinates.lng) !== 0 && (
              <div className="property-coordinates">
                <p><strong>Coordenadas:</strong> {Number(property.coordinates.lat).toFixed(6)}, {Number(property.coordinates.lng).toFixed(6)}</p>
              </div>
            )}
            <MapboxMap
              center={center}
              address={property.address}
              city={property.city}
              state={property.state}
              zipCode={property.zipCode}
              style={mapStyle}
              zoom={15}
            />
          </div>

          <div className="contact-agent">
            <h2>¿Interesado en esta propiedad?</h2>
            <p>Contacte a Noemi Alicea para más información</p>
            <form className="contact-agent-form" onSubmit={handleContactSubmit}>
              <div className="contact-agent-row">
                <input
                  type="text"
                  name="name"
                  value={contactFormData.name}
                  onChange={handleContactChange}
                  placeholder="Nombre *"
                  required
                />
                <input
                  type="email"
                  name="email"
                  value={contactFormData.email}
                  onChange={handleContactChange}
                  placeholder="Correo Electrónico *"
                  required
                />
              </div>
              <input
                type="tel"
                name="phone"
                value={contactFormData.phone}
                onChange={handleContactChange}
                placeholder="Teléfono"
              />
              <textarea
                name="message"
                value={contactFormData.message}
                onChange={handleContactChange}
                placeholder="Mensaje *"
                rows="4"
                required
              ></textarea>
              <button type="submit" className="contact-button" disabled={contactLoading}>
                {contactLoading ? 'Enviando...' : 'Contactar Agente'}
              </button>
              {contactError && <div className="contact-agent-error">{contactError}</div>}
              {contactSubmitted && !contactError && (
                <div className="contact-agent-success">¡Gracias! Su mensaje ha sido enviado exitosamente.</div>
              )}
            </form>
          </div>
        </div>

        {listingType === 'for-sale' && (
          <div className="property-sidebar">
            <MortgageCalculator propertyPrice={property.price} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyDetail;
